import { ComponentType, createElement, isValidElement, ReactNode, SVGProps } from "react";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import { TabPanel } from "@mui/lab";
import { Box, darken, lighten, Skeleton, Stack, Tooltip, Typography as T } from "@mui/material";
import AccessLevelChip from "datasets/components/AccessLevelChip";
import DatasetPageLayout from "datasets/components/DatasetPageLayout";
import useDataset from "datasets/hooks/useDataset";
import usePreviewMode from "datasets/hooks/usePreviewMode";
import useStatistics from "datasets/hooks/useStatistics";
import DescriptionEditor from "datasets/pages/overview/components/DescriptionEditor";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { getAccentColor } from "datasets/utils/color";
import { format } from "date-fns";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import yn from "yn";
import { ClockIcon, HardDrivesIcon, TableIcon } from "~/assets/icons";
import EditableText from "~/components/EditableText";
import useErrorToast from "~/contexts/error-toast";
import useUserPermission from "~/hooks/useUserPermission";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_DATASET_PERMISSIONS_ENABLED } = import.meta.env;

const formatDate = (timestamp: string) => format(new Date(timestamp), "MMM dd, yyyy");

const Stat = ({
  children,
  icon,
  loading = false,
}: {
  children: ReactNode;
  icon: ReactNode | ComponentType<SVGProps<SVGSVGElement>>;
  loading?: boolean;
}) => {
  const IconElement = isValidElement(icon)
    ? icon
    : createElement(icon as ComponentType<SVGProps<SVGSVGElement>>, {
        color: "inherit",
        width: 14,
        height: 14,
      });

  return (
    <Box color="grey.600" display="flex" alignItems="center" gap={0.8} minHeight={22}>
      <Box sx={{ display: "flex", alignItems: "center", color: "grey.500" }}>{IconElement}</Box>
      {loading ? (
        <Skeleton height={18} width={72} />
      ) : (
        <T
          sx={{
            color: "grey.700",
            fontSize: 13.5,
            fontWeight: 500,
            lineHeight: 1.3,
            cursor: "default",
          }}
        >
          {children}
        </T>
      )}
    </Box>
  );
};

const Skeletons = () => (
  <Stack gap={1}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton key={i} variant="text" height={35} />
    ))}
  </Stack>
);

const DatasetOverview = () => {
  const { showError } = useErrorToast();
  const { data: dataset, isLoading: loadingDataset, mutate } = useDataset();
  const { data: stats, isLoading: loadingStats } = useStatistics(dataset?.id);
  const hasEditPermission = useUserPermission("edit:catalog-dataset");
  const canEditDataset = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.OWNER && hasEditPermission
    : hasEditPermission;
  const isPreviewModeEnabled = usePreviewMode();

  const updateTitle = async (title: string) => {
    await mutate(
      async () => {
        try {
          const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset?.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title }),
          });

          if (!resp.ok) {
            throw new Error("Unable to update dataset title");
          }

          const datasetResp: Dataset = await resp.json();

          trackEvent("dataset_title_updated", {
            dataset_id: datasetResp.id,
            dataset_title: datasetResp.title,
          });

          return datasetResp;
        } catch (e: any) {
          console.error(e);
          rumClient?.recordError(e);
          showError(e.message);
        }
      },
      { optimisticData: (data) => ({ ...data!, title }) }
    );
  };

  return (
    <TabPanel value="overview" sx={{ p: 0 }}>
      <DatasetPageLayout>
        <Stack gap={0.5} pt={1} px={1.5}>
          {/* Title */}
          {loadingDataset && <Skeleton variant="rounded" height="40px" width="200px" />}
          {dataset && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <T
                component="h1"
                fontSize={40}
                fontWeight={700}
                color={getAccentColor(dataset.accentColor)}
                lineHeight={1}
                textAlign="start"
              >
                {canEditDataset ? (
                  <EditableText
                    value={dataset.title}
                    placeholder="Untitled"
                    typographySx={{
                      color: "inherit",
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      lineHeight: "inherit",
                      textAlign: "inherit",
                      marginLeft: -1.5,
                    }}
                    handleBlur={(val) => {
                      if (val !== dataset.title) {
                        updateTitle(val);
                      }
                    }}
                  />
                ) : (
                  <Box py={0.5}>{dataset.title}</Box>
                )}
              </T>
            </Box>
          )}

          {/* Metrics bar */}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{
              py: 0.5,
              mb: 1,
            }}
          >
            {loadingDataset || loadingStats ? (
              <Skeleton variant="rounded" height={28} width={420} />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  minWidth: 0,
                }}
              >
                {yn(VITE_DATASET_PERMISSIONS_ENABLED) && dataset && (
                  <Box>
                    <AccessLevelChip
                      showTooltip
                      accessLevel={dataset.access_level}
                      isPreviewModeEnabled={isPreviewModeEnabled}
                      sx={{
                        color: darken(getAccentColor(dataset.accentColor), 0.2),
                        backgroundColor: lighten(getAccentColor(dataset.accentColor), 0.92),
                        fontSize: 12,
                        borderRadius: 1.5,
                        height: 28,
                      }}
                      tooltipPlacement="top"
                    />
                  </Box>
                )}
                {dataset?.table_count !== undefined && (
                  <Tooltip arrow enterDelay={300} placement="top" title="Total tables">
                    <Box>
                      <Stat icon={TableIcon}>
                        {`${dataset.table_count} ${pluralize("Table", dataset.table_count)}`}
                      </Stat>
                    </Box>
                  </Tooltip>
                )}
                <Tooltip arrow enterDelay={300} placement="top" title="Dataset size">
                  <Box>
                    <Stat icon={HardDrivesIcon} loading={loadingStats}>
                      {prettyBytes(stats?.table_total_bytes ?? 0)}
                    </Stat>
                  </Box>
                </Tooltip>
                {dataset?.cloud_providers[0]?.includes("aws") && (
                  <Tooltip
                    arrow
                    enterDelay={300}
                    placement="top"
                    title="This dataset is hosted on AWS"
                  >
                    <Box>
                      <Stat icon={<CloudOutlinedIcon sx={{ fontSize: 18 }} color="inherit" />}>
                        AWS
                      </Stat>
                    </Box>
                  </Tooltip>
                )}
                {dataset?.updated_at && (
                  <Tooltip arrow enterDelay={300} placement="top" title="Last updated">
                    <Box>
                      <Stat icon={ClockIcon}>{formatDate(dataset.updated_at)}</Stat>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          {/* Description */}
          {loadingDataset && <Skeletons />}
          {dataset && <DescriptionEditor dataset={dataset} canEdit={canEditDataset} />}
        </Stack>
      </DatasetPageLayout>
    </TabPanel>
  );
};

export default DatasetOverview;
